import { CargoCharacteristicIdentity } from 'Models/Entities/CargoCharacteristicEntity';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { BookingWizardData } from './BaseBookingWizardData';
import { BookingAddOn, hasAddOnsChanged } from './BookingAddOn';
import { hasPassengersChanged } from './PassengerBookingWizardData';
import { getDefaultPassengerInfo, PassengerDetail } from './PassengerDetail';

export class VehicleBookingWizardData {
	type: 'vehicle';

	// Passenger tickets
	adultTickets: PassengerDetail[];
	childTickets: PassengerDetail[];
	infantTickets: PassengerDetail[];
	passengerDTickets?: PassengerDetail[];
	passengerETickets?: PassengerDetail[];
	passengerFTickets?: PassengerDetail[];
	passengerGTickets?: PassengerDetail[];
	passengerHTickets?: PassengerDetail[];

	// Additional details
	note?: string;

	// Admin-configured dynamic field values, keyed by DynamicBookingFieldEntity.Id (uuid).
	dynamicFieldValues?: Record<string, string>;

	// Additional options
	departingTripOptions: BookingAddOn[];
	returningTripOptions: BookingAddOn[];

	/**
	 * Used in ferry filter.
	 */
	trailerCheckboxSelected: boolean;

	// Driver information
	driverFirstName?: string;
	driverLastName?: string;
	driverPhone?: string;
	customerEmail?: string;

	// Vehicle and trailer fields
	cargoIdentification?: string;
	hiredVehicle?: boolean;
	showWidthDetails?: boolean;
	cargoTypeId?: string;
	cargoMake?: string;
	cargoModel?: string;
	cargoCharacteristics: CargoCharacteristicIdentity[] = [];
	// Inherited vehicle characteristics (from the cargo type) the user has removed for this booking.
	// Only editable when the booking customer has the selected cargo type attached. Sent to the server
	// and subtracted as the final step when resolving effective characteristics.
	removedInheritedCharacteristics: CargoCharacteristicIdentity[] = [];
	vehicleWidthId?: string;
	vehicleLengthId?: string;
	vehicleWeightId?: string;
	vehicleExactWeight?: number | null;
	vehicleExactWeightUnit?: string | null;
	trailerLengthId?: string;
	trailerWeightId?: string;
	trailerTypeId?: string;
	trailerWidthId?: string;
	showTrailerWidthDetails?: boolean;
	trailerExactWeight?: number | null;
	trailerExactWeightUnit?: string | null;
	trailerCargoCharacteristics: CargoCharacteristicIdentity[] = [];

	// Marks that the customer typed in the field — used to suppress
	// pre-fill when the cargo/tow-on type changes. Not persisted server-side.
	vehicleExactWeightUserEdited?: boolean;
	trailerExactWeightUserEdited?: boolean;

	constructor(data: Partial<VehicleBookingWizardData>) {
		Object.assign(this, data);
		if (data.departingTripOptions) {
			this.departingTripOptions = data.departingTripOptions.map(x => new BookingAddOn(x));
		}
		if (data.returningTripOptions) {
			this.returningTripOptions = data.returningTripOptions.map(x => new BookingAddOn(x));
		}
	}
}

export function getDefaultVehicleWizardData(): VehicleBookingWizardData {
	return {
		type: 'vehicle',
		adultTickets: [getDefaultPassengerInfo('A', true)],
		childTickets: [],
		infantTickets: [],
		passengerDTickets: [],
		passengerETickets: [],
		passengerFTickets: [],
		passengerGTickets: [],
		passengerHTickets: [],
		note: '',
		dynamicFieldValues: {},
		departingTripOptions: [],
		returningTripOptions: [],
		cargoIdentification: '',
		hiredVehicle: false,
		cargoTypeId: '',
		cargoMake: '',
		cargoModel: '',
		cargoCharacteristics: [],
		removedInheritedCharacteristics: [],
		vehicleLengthId: '',
		vehicleWeightId: '',
		vehicleExactWeight: null,
		vehicleExactWeightUnit: null,
		trailerLengthId: '',
		trailerWeightId: '',
		trailerTypeId: 'NO_TRAILER',
		trailerWidthId: '',
		showTrailerWidthDetails: false,
		trailerExactWeight: null,
		trailerExactWeightUnit: null,
		trailerCargoCharacteristics: [],
		vehicleExactWeightUserEdited: false,
		trailerExactWeightUserEdited: false,
		driverFirstName: '',
		driverLastName: '',
		driverPhone: '',
		trailerCheckboxSelected: false,
	};
}

export function isVehicleBookingWizardData(data: any): data is VehicleBookingWizardData {
	return data instanceof VehicleBookingWizardData && data.type === 'vehicle';
}

export function getWizardDataVehicleWizardDataDependencies(
	properties: (keyof VehicleBookingWizardData)[],
	wizardData?: BookingWizardData,
) {
	if (isNullOrUndefined(wizardData)) {
		return [];
	}
	const innerData = wizardData.innerWizardData as VehicleBookingWizardData;

	return properties.map(x => innerData[x]);
}

export function vehicleBookingWizardDataHasChanged(
	newInnerWizardData: VehicleBookingWizardData,
	oldInnerWizardData: VehicleBookingWizardData,
): boolean {
	// Check passenger changes
	if (hasPassengersChanged(newInnerWizardData, oldInnerWizardData)) {
		return true;
	}

	// Check differences of add-ons in departure trip
	if (hasAddOnsChanged(newInnerWizardData.departingTripOptions, oldInnerWizardData.departingTripOptions)) {
		return true;
	}

	// Check differences of add-ons in return trip
	if (hasAddOnsChanged(newInnerWizardData.returningTripOptions, oldInnerWizardData.returningTripOptions)) {
		return true;
	}

	// Check if the customer-entered note has changed
	if ((newInnerWizardData.note ?? '') !== (oldInnerWizardData.note ?? '')) {
		return true;
	}

	// Check if vehicle info has changed
	if (newInnerWizardData.vehicleLengthId !== oldInnerWizardData.vehicleLengthId) {
		return true;
	}
	if (newInnerWizardData.vehicleWeightId !== oldInnerWizardData.vehicleWeightId) {
		return true;
	}
	if (newInnerWizardData.vehicleWidthId !== oldInnerWizardData.vehicleWidthId) {
		return true;
	}
	if (newInnerWizardData.cargoMake !== oldInnerWizardData.cargoMake) {
		return true;
	}
	if (newInnerWizardData.cargoModel !== oldInnerWizardData.cargoModel) {
		return true;
	}
	if (newInnerWizardData.cargoTypeId !== oldInnerWizardData.cargoTypeId) {
		return true;
	}
	if (newInnerWizardData.cargoIdentification !== oldInnerWizardData.cargoIdentification) {
		return true;
	}
	if (haveCharacteristicsChanged(newInnerWizardData.cargoCharacteristics, oldInnerWizardData.cargoCharacteristics)) {
		return true;
	}
	if (
		haveCharacteristicsChanged(
			newInnerWizardData.removedInheritedCharacteristics,
			oldInnerWizardData.removedInheritedCharacteristics,
		)
	) {
		return true;
	}
	if (newInnerWizardData.hiredVehicle !== oldInnerWizardData.hiredVehicle) {
		return true;
	}

	// Customer-entered exact weight for the vehicle (value or unit change counts).
	if ((newInnerWizardData.vehicleExactWeight ?? null) !== (oldInnerWizardData.vehicleExactWeight ?? null)) {
		return true;
	}
	if ((newInnerWizardData.vehicleExactWeightUnit ?? null) !== (oldInnerWizardData.vehicleExactWeightUnit ?? null)) {
		return true;
	}

	// Check if vehicle driver info has changed
	if (newInnerWizardData.driverFirstName !== oldInnerWizardData.driverFirstName) {
		return true;
	}
	if (newInnerWizardData.driverLastName !== oldInnerWizardData.driverLastName) {
		return true;
	}
	if (newInnerWizardData.driverPhone !== oldInnerWizardData.driverPhone) {
		return true;
	}

	// Check if trailer info has changed
	if (newInnerWizardData.trailerLengthId !== oldInnerWizardData.trailerLengthId) {
		return true;
	}
	if (newInnerWizardData.trailerWeightId !== oldInnerWizardData.trailerWeightId) {
		return true;
	}
	if (newInnerWizardData.trailerTypeId !== oldInnerWizardData.trailerTypeId) {
		return true;
	}
	if (newInnerWizardData.trailerWidthId !== oldInnerWizardData.trailerWidthId) {
		return true;
	}
	if ((newInnerWizardData.trailerExactWeight ?? null) !== (oldInnerWizardData.trailerExactWeight ?? null)) {
		return true;
	}
	if ((newInnerWizardData.trailerExactWeightUnit ?? null) !== (oldInnerWizardData.trailerExactWeightUnit ?? null)) {
		return true;
	}
	if (
		haveCharacteristicsChanged(
			newInnerWizardData.trailerCargoCharacteristics,
			oldInnerWizardData.trailerCargoCharacteristics,
		)
	) {
		return true;
	}

	return false;
}

/**
 * Cargo characteristics are stored as `{ characteristicId, name }[]`. The
 * order isn't significant, so compare as a set of ids.
 */
function haveCharacteristicsChanged(
	a: { characteristicId: string }[] | undefined,
	b: { characteristicId: string }[] | undefined,
): boolean {
	const aIds = new Set((a ?? []).map(x => x.characteristicId));
	const bIds = new Set((b ?? []).map(x => x.characteristicId));
	if (aIds.size !== bIds.size) return true;
	for (const id of aIds) {
		if (!bIds.has(id)) return true;
	}
	return false;
}
